import { Component, Inject, Injector, Input } from '@angular/core';
import { FormControl, Validators } from "@angular/forms";
import { OptionType } from "ng/src/app/core/Option";
import { Utils } from "ng/src/app/core/utils";
import { BaseComponent } from "ng/src/app/shared/base.component";
import { BasicRecord, Nullish } from "ng/src/app/shared/model/app.model";
import { ReadonlyContainerEntry, TemplateContainer } from "ng/src/app/templates/shared/template-container/template-container.model";
import { Formula, TemplateValueCalculator } from "ng/src/app/templates/shared/template-value-calculator.model";
import { TemplateSegment, TemplateSegmentTypeId, TemplateTypeId } from 'ng/src/app/templates/shared/template.model';
import { TreeNode } from "primeng/api";
import ExtValueKey = Formula.ExtValueKey;

@Component({
  selector: 'lb-formula-editor[scope][logTimeZone]',
  templateUrl: './formula-editor.component.html',
  styleUrls: ['./formula-editor.component.scss']
})
export class FormulaEditorComponent extends BaseComponent {

  @Input()
  set scope(val: FormulaEditorScope){
    this._segment = val.segment;
    this._segmentMap = Utils.arrayToMap(val.templateContainer.flatten()
      .map(item => item.segment)
      .filter(Nullish.isNotNullish),
      'id');
    
    this._templateContainer = val.templateContainer;
    this.load();
  }
  
  @Input()
  logTimeZone!: string;
  
  mode: 'formula' | 'variable' = 'formula';
  
  readonly externalVariables = Formula.ExtValues;
	
  formula = '';
	decimalPlaces: number | null = null;
	
  testValues: BasicRecord<any> = {};
  variables = new Array<Formula.Variable>();
  
  testResult = '';
  testResultError = '';
  
  availableVariableReferences: Array<TreeNode<TemplateSegment | Formula.ExtValueKey | undefined>> = [];
  newVariableSegment?: TemplateSegment | Formula.ExtValueKey | null;
	
  private _templateContainer!: TemplateContainer;
	private _executionPromise?: Promise<unknown>;
  
  get newVariableNode() {
    return this._newVariableNode;
  }
  
  set newVariableNode(val: TreeNode<TemplateSegment | undefined> | undefined) {
    this._newVariableNode = val;
    
    if (val) {
      this.newVariableSegment = val.data;
    }
    else {
      this.newVariableSegment = null;
    }
  }
  
  private _newVariableNode?: TreeNode<TemplateSegment | undefined>;
  
  variableNameFormControl = new FormControl<string>('', [
    Validators.required,
    (s) => {
      const value = s.value;
      if (!this.stringIsNullOrEmpty(value)) {
        if (Formula.namePattern.test(value)) {
          return null;
        }
        
        return { 'Invalid variable name': true};
      }
      
      return { 'Cannot be empty': true };
    },
    (s) => {
      if (this.variables.find(v => v.name.toLowerCase() === s.value.toLowerCase())) {
        return { 'Variable name already exists': true };
      }
      
      return null;
    }
  ]);
  
  _segmentMap!: Record<string, TemplateSegment>;
  private _segment!: TemplateSegment;
  
  constructor(
    injector: Injector,
    @Inject('templateValueCalculator') private calculationService: TemplateValueCalculator
  ) {
    super(injector);
  }
  
  load() {
    const formulaScope = Formula.parseFormula(this._segment);
    this.variables = formulaScope.variables;
    this.formula = formulaScope.formula;
		this.decimalPlaces = formulaScope.decimalPlaces ?? null;
    
    this.updateAvailableVariableReferences();
  }
  
  addVariable(): boolean {
    if (this.newVariableSegment == null) {
      throw new Error('No variable segment selected');
    }
    
    this.variableNameFormControl.markAsDirty();
    if (this.variableNameFormControl.invalid) {
      return false;
    }
    
		const type = typeof this.newVariableSegment === 'string' ? 'external' : 'variable';
		
    const name = this.variableNameFormControl.value;
		
    const variable: Formula.Variable = {
      name: name!,
      reference: typeof this.newVariableSegment === 'string' ? this.newVariableSegment : this.newVariableSegment.id,
	    type
    }
    
    this.variables = [
      ...this.variables,
      variable
    ];
    
    this.newVariableSegment = null;
    this.updateAvailableVariableReferences();
    return true;
  }
  
  removeVariable(variable: Formula.Variable) {
    this.variables = this.variables.filter(v => v !== variable);
    this.updateAvailableVariableReferences();
  }
  
  updateAvailableVariableReferences() {
    const usedIds = this.variables.map(v => {
      return v.reference;
    });
    
    const usedIdSet = new Set([this._segment.id, ...usedIds]);
		
		const container = this._templateContainer;
		const segment = this._segment;
		
		let usingNodes = this._templateContainer.getNonObsoleteView().entries;
		if (container.templateTypeId === TemplateTypeId.OPERATIONAL) {
			const node = container.entryMap[segment.id];
			usingNodes = node.parent?.entries ?? [];
		}
		
    const nodes = usingNodes.reduce((arr, entry) => {
        const createItem = (item: ReadonlyContainerEntry): TreeNode<TemplateSegment | undefined> | undefined => {

          const isLeaf = item.entries.length == 0;
          const selectable = isLeaf && !usedIdSet.has(item.segment?.id ?? -1);
          
          return {
            selectable,
            leaf: isLeaf,
            label: OptionType.nonNull(item.segment)
	            .map(s => s.name ?? s.type.name)
	            .getOrElse(`Intro to ${container.templateName}`),
            data: item.segment,
	          ...container.templateTypeId === TemplateTypeId.INSPECTION && {
	            children: item.entries
		            .filter(n => n.segment?.obsolete !== true)
		            .map(createItem)
		            .filter(Nullish.isNotNullish)
	          }
          };
        };
        
        const item = createItem(entry);
        
        if (item) {
          arr.push(item);
        }
        
        return arr;
      }, [] as TreeNode[]);
		
		const extProps: TreeNode<ExtValueKey>[] = Object.entries(Formula.ExtValues)
			.map(([key, name]) => {
				return {
					label: name,
					data: key as keyof typeof Formula.ExtValues,
					leaf: true,
					selectable: !usedIdSet.has(key as keyof typeof Formula.ExtValues)
				};
			});
		
		const extNode: TreeNode<ExtValueKey | undefined> = {
			label: 'Note Properties',
			data: undefined,
			leaf: false,
			selectable: false,
			children: extProps
		}
		
		this.availableVariableReferences = [
			extNode,
			...nodes
		];
  }
  
  execute(setOptionalError = false) {
    this.testResultError = '';
    this.testResult = '';
    
    const copySegment: TemplateSegment = {
      ...this._segment,
      formula: Formula.scopeToString(this.getFormulaScope())
    }
		
		if (this.canUseDecimalPlaces()) {
			copySegment.decimalPlaces = this.decimalPlaces ?? null;
		}
    
    const graph = this.calculationService.calculateDependencyGraph([copySegment]);
    const errorMap: Record<string, string> = {};
    
		let nonOptionalMissing = true;
		
	  const pseudoNote: BasicRecord = {};
		for (const v of this.variables) {
			if (!nonOptionalMissing) {
				break;
			}
			const {reference} = v;
			
			if (typeof reference === 'number') {
				// Add empty values for all dependencies if they are missing due to calculation service
				// requiring keys present.
				const testValue = this.testValues[reference];
				if (testValue == null || testValue === '') {
					this.testValues[reference] = '';
					if (!v.optional) {
						errorMap[this._segment.id] = `Missing non-optional values`;
						nonOptionalMissing = false;
						break;
					}
					
				}
			}
			else if (this.keyIn(this.externalVariables, reference)) {
				const testValue = this.testValues[reference];
				pseudoNote[reference] = testValue;
				
				if (!v.optional && (testValue == null || testValue === '')) {
					errorMap[this._segment.id] = `Missing non-optional values`;
					nonOptionalMissing = false;
					break;
				}
			}
		}
	  
	  if (!nonOptionalMissing) {
			if (setOptionalError) {
			  this.testResultError = 'Missing non-optional values';
			}
		  return;
	  }
    
    const segmentMap = Utils.arrayToMap(
      Object.values(this._segmentMap),
      ({ id, longName }) => ['id', { id, longName }]
    );
    
    const promise = this.calculationService.calculateValues(
      graph,
      segmentMap,
      this.testValues,
      errorMap,
      {},
      [],
      this._segment.logId,
      this.logTimeZone,
      pseudoNote
    );
		
		this._executionPromise = promise;
    
    promise.then((r) => {
			if (this._executionPromise !== promise) {
        return;
      }
			
      this.testResultError = '';
      let loggingError = '';
			
      if (Array.isArray(r)) {
        r.map(OptionType.nonNull)
          .filter(OptionType.isSome)
          .map((o) => {
            if (o.value instanceof Error) {
              const str = `${o.value.message}\n${o.value.stack}\n`;
							if (o.value.message.length) {
                this.testResultError += `${o.value.message}\n`;
							}
              loggingError += str;
            }
          })
      }
			
      const sId = this._segment.id;
      
      Utils.guardLet(errorMap[sId], err => {
				if (!this.testResultError.includes(err)) {
          this.testResultError += `${err}\n`;
				}
      });
      
			if (loggingError.length > 0) {
        console.error(loggingError);
      }
      
      this.testResult = this.testValues[this._segment.id] ?? '';
    });
  }
	
	getReferenceLabelForVariable(variable: Formula.Variable) {
		return Formula.getReferenceLabelForVariable(variable, this._segmentMap);
	}
  
  getFormulaScope(): Formula.Scope {
    const formula: Formula.Scope = {
      variables: [...this.variables],
      formula: this.formula
    }
		
		if (this.canUseDecimalPlaces()) {
			formula.decimalPlaces = this.decimalPlaces ?? null;
		}
		
		return formula;
  }
	
	canUseDecimalPlaces() {
		return this._segment.type.id === TemplateSegmentTypeId.NUMERIC_INPUT;
	}
}

export type FormulaEditorScope = {
  segment: TemplateSegment,
  templateContainer: TemplateContainer,
}
